import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {BehaviorSubject, Subject, takeUntil} from 'rxjs';
import moment from 'moment';
import {environment} from '../environments/environment';
import {Promotion} from '../models/promotion';
import {SKIP_ERROR_INTERCEPTOR} from '../app/interceptors/error.interceptor';
import {StorageService} from './storage-service';

const API = environment.API;

@Injectable({
  providedIn: 'root',
})
export class PromotionService implements OnDestroy {
  onDestroy$ = new Subject<boolean>();

  promotionCode$ = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient,
              private storage: StorageService) {

    const promotionCode = this.storage.get('promotionCode');
    if (promotionCode) {
      this.promotionCode$.next(promotionCode);
    }

    this.promotionCode$.pipe(takeUntil(this.onDestroy$)).subscribe(promotionCode => {
      this.storage.add(StorageService.PROMOTION_CODE, promotionCode);
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  createAmountPromotion(code: string, ref: string, description: string, amount: number, productIds: string[], limitation: any, begin: Date, end: Date, note: string, preference: any) {
    const body = {code, ref, description, amount, productIds, limitation, begin, end, note, preference};
    return this.http.post(`${API}/api/promotions/amount`, body);
  }

  createPercentagePromotion(code: string, ref: string, description: string, percentage: number, productIds: string[], limitation: any, begin: Date, end: Date, note: string, preference: any) {
    const body = {code, ref, description, percentage, productIds, limitation, begin, end, note, preference};
    return this.http.post(`${API}/api/promotions/percentage`, body);
  }

  createFixedPromotion(code: string, ref: string, description: string, fixed: number, productIds: string[], limitation: any, begin: Date, end: Date, note: string, preference: any) {
    const body = {code, ref, description, fixed, productIds, limitation, begin, end, note, preference};
    return this.http.post(`${API}/api/promotions/fixed`, body);
  }

  createCreditPromotion(code: string, ref: string, description: string, visitDuration: number, guestDuration: number, productIds: string[], limitation: any, begin: Date, end: Date, note: string, preference: any) {
    const body = {code, ref, description, visitDuration, guestDuration, productIds, limitation, begin, end, note, preference};
    return this.http.post(`${API}/api/promotions/credit`, body);
  }

  createReservePromotion(code: string, ref: string, description: string, visitDuration: number, guestDuration: number, productIds: string[], limitation: any, begin: Date, end: Date, note: string, preference: any) {
    const body = {code, ref, description, visitDuration, guestDuration, productIds, limitation, begin, end, note, preference};
    return this.http.post(`${API}/api/promotions/reserve`, body);
  }

  getById(id: string) {
    return this.http.get<Promotion>(`${API}/api/promotions/` + id);
  }

  getByCode(code: string, region?: string) {
    const params: any = {}
    if (region) {
      params.region = region;
    }
    const context = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    return this.http.get<Promotion>(`${API}/api/promotions/${code.toUpperCase()}/code`, {params, context});
  }

  getByRef(ref: string, region?: string) {
    const params: any = {}
    if (region) {
      params.region = region;
    }
    const context = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    return this.http.get<Promotion>(`${API}/api/promotions/${ref}/ref`, {params, context});
  }

  validate(code: string, productIds: any[], region: string | null) {
    const params: any = {productIds, region};
    const context = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    return this.http.get<Promotion>(`${API}/api/promotions/${code.toUpperCase()}/validate`, {params, context});
  }

  findPromotions(limit: number, skip: number) {
    const params = {limit, skip};
    return this.http.get<Promotion[]>(`${API}/api/promotions`, {params});
  }

  findActive(limit: number, skip: number) {
    const params = {limit, skip};
    return this.http.get<Promotion[]>(`${API}/api/promotions/active`, {params});
  }

  findExpired(limit: number, skip: number) {
    const params = {limit, skip};
    return this.http.get<Promotion[]>(`${API}/api/promotions/expired`, {params});
  }

  findFuture(limit: number, skip: number) {
    const params = {limit, skip};
    return this.http.get<Promotion[]>(`${API}/api/promotions/future`, {params});
  }

  search(search: string, excludeIds: string[], limit: number, skip: number) {
    const params = {search, excludeIds, limit, skip};
    return this.http.get(`${API}/api/promotions/search`, {params});
  }

  updatePromotion(id: string, code: string, ref: string, description: string, productIds: string[], limitation: any, begin: Date, end: Date, note: string, preference: any) {
    const body = {code, ref, description, productIds, limitation, begin, end, note, preference};
    return this.http.put(`${API}/api/promotions/` + id, body);
  }

  terminate(id: string) {
    return this.http.put(`${API}/api/promotions/${id}/terminate`, {});
  }

  deactivate(id: string) {
    return this.http.put(`${API}/api/promotions/${id}/deactivate`, {});
  }

}
